/**
 * Google Tag Manager (GTM) bootstrap for the MDIE embed.
 *
 * @remarks
 *
 * The MDIE app ships as a Web Component (`<market-data-center>`) that renders
 * inside a Shadow DOM for style isolation. GTM's standard install expects two
 * fragments in the *host page*: a `<script>` as high as possible in `<head>`,
 * and a `<noscript>` iframe immediately after the opening `<body>`.
 *
 * A Shadow DOM has no `<head>`/`<body>`, and a `<script>` inserted into a shadow
 * root is inert (it never executes). GTM state (`window.dataLayer`) also lives
 * on the single global `window`, and GTM Preview / Tag Assistant only inspect
 * the host document. So we inject into the **host document** (`document.head`
 * and `document.body`), mirroring how `ensureFontFaces` targets the owner
 * document rather than the shadow root.
 *
 * Injection is idempotent: repeated calls (multiple embeds on a page, or app
 * re-renders) initialize GTM exactly once. When `gtmId` is empty the functions
 * are strict no-ops, which is also the disable/rollback path.
 */

const GTM_SCRIPT_ATTRIBUTE = 'data-mdc-gtm';

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

/**
 * Discriminated union of every analytics event the app emits.
 *
 * @remarks
 *
 * All event names keep the existing `mdc_` prefix convention, and every event
 * carries `page_path` so the container can attribute it to a SPA route. Routing
 * all pushes through a single typed entry point ({@link pushEvent}) keeps event
 * names and parameter keys consistent, which GA4/GTM rely on. Scroll depth and
 * time-on-page are intentionally out of scope for this phase.
 */
export type MdcEvent =
  | { event: 'mdc_page_view'; page_path: string }
  | {
      event: 'mdc_click';
      /** Stable low-cardinality descriptor OR the `data-track` value. */
      element: string;
      /** Human-readable label (text / aria-label / `data-track-label`). */
      label?: string;
      /** Optional grouping bucket, from `data-track-context`. */
      context?: string;
      page_path: string;
    }
  | {
      event: 'mdc_search_submit';
      /**
       * Raw search term the user submitted (approved PII exception). Code-
       * emitted from the search form's submit handler, so it only fires on a
       * real, non-empty search — never with an empty or undefined term.
       */
      search_term: string;
      /** Which search bar: `search_bar` (nav) or `homepage_search` (hero). */
      context?: string;
      page_path: string;
    }
  | {
      event: 'mdc_link_click';
      element: string;
      label?: string;
      context?: string;
      /** Resolved `href` of the anchor (relative for TanStack `<Link>`). */
      href?: string;
      /** `external` when `target="_blank"` or cross-origin; else `internal`. */
      link_type: 'internal' | 'external';
      page_path: string;
    }
  | {
      event: 'mdc_tooltip_view';
      element: string;
      label?: string;
      context?: string;
      /** Glossary term identifier, from `data-track-term-slug` (enrichment). */
      term_slug?: string;
      page_path: string;
    }
  | {
      event: 'mdc_search_results_shown';
      /** Count of selectable suggestions shown. */
      result_count: number;
      /** Length only — never the query text (PII). */
      query_length: number;
      page_path: string;
    }
  | {
      event: 'mdc_search_result_select';
      /** 0-based position of the chosen row in the dropdown. */
      result_position: number;
      /** Suggestion count at selection time. */
      result_count: number;
      /** Public market-data id (ticker/name) — safe to send. */
      result_id?: string | null;
      /** `equity` | `etf` | `mutual_fund`. */
      result_type?: string;
      /** Which row was chosen. */
      result_kind: 'top_result' | 'suggestion_row' | 'view_all';
      /** Length only — never the query text (PII). */
      query_length: number;
      href?: string;
      page_path: string;
    };

/**
 * Ensures `window.dataLayer` exists and returns it.
 */
function ensureDataLayer(): Record<string, unknown>[] {
  window.dataLayer = window.dataLayer ?? [];
  return window.dataLayer;
}

/**
 * Pushes a typed analytics event onto the dataLayer.
 *
 * @remarks
 *
 * Single choke point for every analytics push. Routing all events through one
 * typed function prevents ad-hoc `dataLayer.push` calls from drifting in shape.
 * Like {@link pushPageView}, this appends regardless of whether GTM was
 * initialized (an empty dataLayer is valid; events are simply never consumed by
 * a container when GTM is disabled for an environment).
 *
 * @param payload - A typed {@link MdcEvent} to push.
 */
export function pushEvent(payload: MdcEvent): void {
  // Dev-only debug logging. `pushEvent` is the single choke point for every
  // analytics push, so one log here surfaces all event types (page views,
  // clicks, link clicks, tooltip views, search events). Gated on
  // `import.meta.env.DEV` so it never reaches QA/PROD bundles.
  if (import.meta.env.DEV) {
    console.debug(`[mdc-track] ${payload.event}`, payload);
  }

  const dataLayer = ensureDataLayer();
  dataLayer.push(payload);
}

/**
 * Initializes Google Tag Manager on the host document.
 *
 * Injects the loader `<script>` into `<head>` and the `<noscript>` iframe into
 * `<body>`, matching the container's official snippet. Safe to call multiple
 * times; only the first call with a given container ID injects. No-ops when
 * `gtmId` is empty.
 *
 * @param gtmId - The GTM container ID (e.g. "GTM-W39MB393"). Empty, missing, or
 *   non-string (e.g. a `config.json` that omits the `gtmId` key) disables GTM.
 * @param targetDocument - Document to inject into. Defaults to the host document.
 */
export function initGtm(
  gtmId: string | null | undefined,
  targetDocument: Document = document,
): void {
  // Runtime config comes from config.json, which may omit `gtmId` entirely
  // (older deploys, disabled environments). Guard against non-string values so
  // a missing key is a clean no-op rather than a thrown TypeError that silently
  // aborts boot-time injection.
  const trimmedId = typeof gtmId === 'string' ? gtmId.trim() : '';

  if (!trimmedId) {
    return;
  }

  // Idempotency guard: never inject the same container twice, even across
  // multiple embeds or hot re-renders. Scope the check to the loader <script>
  // we own (qualified by tag) rather than a bare attribute selector, so an
  // unrelated element carrying the same attribute on the host (Drupal) page
  // can't make this a false-positive no-op.
  const alreadyInjected = targetDocument.head.querySelector(
    `script[${GTM_SCRIPT_ATTRIBUTE}]`,
  );

  if (alreadyInjected) {
    return;
  }

  // Prime the dataLayer with the `gtm.start` event exactly as GTM's snippet
  // does before the loader script runs.
  const dataLayer = ensureDataLayer();
  dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

  // 1. Loader <script>, as high as possible in <head>.
  const script = targetDocument.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(
    trimmedId,
  )}`;
  script.setAttribute(GTM_SCRIPT_ATTRIBUTE, '');

  const head = targetDocument.head;
  head.insertBefore(script, head.firstChild);

  // 2. <noscript> iframe, immediately after the opening <body>.
  const noscript = targetDocument.createElement('noscript');
  noscript.setAttribute(GTM_SCRIPT_ATTRIBUTE, '');
  const iframe = targetDocument.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(
    trimmedId,
  )}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);

  const body = targetDocument.body;
  body.insertBefore(noscript, body.firstChild);
}

/**
 * Pushes a page-view event onto the dataLayer.
 *
 * @remarks
 *
 * MDIE is a single-page app: TanStack Router changes routes without a full page
 * load, so GTM's built-in page-load trigger only fires once. Pushing an explicit
 * event per navigation lets the container track in-app route changes. No-ops
 * when GTM was never initialized (empty dataLayer is still valid, but we guard
 * against firing before init to keep events aligned with the loader).
 *
 * @param path - The route path being viewed (e.g. "/equity/AAPL").
 */
export function pushPageView(path: string): void {
  pushEvent({ event: 'mdc_page_view', page_path: path });
}
